/**
 * Per-run agent trace — the tool-call timeline derived from the same stored log
 * records the logs drill-in renders (issue #155, spec
 * `docs/superpowers/specs/2026-10-17-watchtower-agent-trace-design.md` §6).
 *
 * WHAT THIS VIEW IS FOR. On the measured session, tools were executing for
 * 12.7s inside a 262.7s wall clock — 5%. Token and cost telemetry prices the
 * other 95% and never locates it. The gaps BETWEEN the bars are that 95%, which
 * is the whole reason the view exists; the summary ratio and the one line of
 * copy under the timeline are not decoration.
 *
 * COLLAPSED BY DEFAULT, FETCHING ONLY ON FIRST EXPAND — load-bearing, not a
 * preference (spec §6). The route does not paginate and derives over the whole
 * session: one call lists the entire session prefix and GETs every object
 * sequentially inside one Lambda invocation. `useTrace`'s `enabled` flag stays
 * `false` until the disclosure is opened, and once opened it STAYS `true` so
 * collapsing and re-opening serves the cache instead of re-reading S3.
 *
 * READ-ONLY, and it interprets nothing. Marker attributes are VERBATIM WIRE
 * SCALARS (spec §4.5): `is_plugin` arrives as the string `'false'` and
 * `duration_ms` as `'70'` on the JSON wire, and `Boolean('false')` is `true`.
 * The derivation module's `boolAttr` / `intAttr` are NOT importable here —
 * `dashboard/` mirrors shared types by hand and does not import from `lib/` —
 * so nothing in this file coerces a marker attribute. They are rendered as
 * text, which needs no coercion to be correct.
 *
 * COPY RULES ARE NORMATIVE (spec §4.6). An `unterminated` span says "no result
 * recorded" and must never say or imply the agent died or crashed: our own
 * `LOG_ATTRS_MAX` attribute cap, the receiver's 256-records-per-request cap, the
 * INFO severity floor and this plane's own TWO bounds (`TRACE_MAX_RECORDS` and
 * the byte budget behind the same `truncated` flag) can EACH manufacture one —
 * FIVE causes, not four. A span carrying `attrs_truncated` says "attributes
 * truncated". Failures are the only status-coloured element.
 */

import { useState } from 'react';
import {
  useTrace,
  type AgentTrace,
  type MarkerEventName,
  type SpanState,
  type TraceMarker,
  type TraceSpan,
} from '../lib/queries';

// ---------------------------------------------------------------------------
// Formatting.
// ---------------------------------------------------------------------------

/** A duration for a human: ms under a second, one decimal of seconds under a
 *  minute, m+s above. `null` is an em dash — never `0ms`, which would read as a
 *  measured instant rather than as an absent measurement. */
export function fmtMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}

/** Axis labels prefer whole seconds — `fmtMs` would print `0ms` at the origin
 *  and `1m 00s` at a minute, which reads as a duration rather than a position
 *  on a clock. */
export function fmtAxis(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const total = Math.round(ms / 1000);
  const s = total % 60;
  return s === 0 ? `${total / 60}m` : `${Math.floor(total / 60)}m ${s}s`;
}

/** `tool_busy_ratio` is a FRACTION (0.048), rendered as a percentage. */
export function fmtRatio(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Span state — a CLOSED union, so this switch is exhaustive.
// ---------------------------------------------------------------------------

/**
 * The short label beside a span's bar, and the copy that carries spec §4.6.
 *
 * TWO guards, for two different edits, and they are NOT interchangeable:
 *
 * - `const _exhaustive: never` in the `default` arm makes adding a fourth
 *   member to the MIRRORED union, here inside `dashboard/`, a type error. This
 *   repo has twice shipped a widened union into an else-arm with `tsc` clean (a
 *   cadence rule labelled "run", rendering `undefined undefined`).
 * - The runtime arm handles the edit `tsc` CANNOT see: the server widens
 *   `SpanState` in `lib/lambdas/shared/agent-trace.ts` and the mirror here is
 *   updated later, or not at all. `dashboard/` does not import from `lib/`, so
 *   nothing type-checks the two against each other and the new member arrives
 *   as DATA at runtime.
 *
 * It used to `throw` there, and the comment claimed `tsc` covered the case —
 * which was false for exactly the server-side widening that is the only way to
 * reach it. There is no error boundary anywhere in `dashboard/src`, so a throw
 * inside a row renderer unmounts the whole Runs tab: a white screen instead of
 * one unfamiliar label. So it DEGRADES — it renders the raw value, which is
 * honest, invents nothing, and tells the reader what the server actually sent.
 * The mirror itself is pinned by `test/trace-console-mirror.test.ts` in the root
 * suite, which is the only place that can compare the two.
 */
export function spanStateLabel(state: SpanState): string | null {
  switch (state) {
    case 'complete':
      return null;
    case 'unterminated':
      // NEVER "the agent died" / "crashed" / "was killed" — four Watchtower-side
      // caps can each produce this state (spec §4.6).
      return 'no result recorded';
    case 'orphan_result':
      return 'no decision recorded';
    default: {
      const _exhaustive: never = state;
      return String(_exhaustive);
    }
  }
}

/** The bar's CSS class per state. `complete` is tool-coloured; the other two are
 *  deliberately NOT status-coloured — failures are the only coloured status. An
 *  unknown state gets the SAME neutral class as `unterminated` rather than a new
 *  one: a state we have never seen is not a status we can colour, and inventing
 *  a colour for it would be the fabrication §4.5 bans one layer up. See
 *  `spanStateLabel` for why neither of these may throw. */
export function spanStateClass(state: SpanState): string {
  switch (state) {
    case 'complete':
      return 'trace-bar-complete';
    case 'unterminated':
      return 'trace-bar-unterminated';
    case 'orphan_result':
      return 'trace-bar-orphan';
    default: {
      const _exhaustive: never = state;
      void _exhaustive;
      return 'trace-bar-unterminated';
    }
  }
}

// ---------------------------------------------------------------------------
// Marker event names — an OPEN set. The `Record` pins the MAPPED union; the
// `default` handles everything else.
// ---------------------------------------------------------------------------

/**
 * Human labels for the mapped marker events. Typed as a total `Record` over
 * `MarkerEventName`, so mirroring a new allowlist key without labelling it is a
 * type error — the `never`-arm discipline applied to the half of the problem
 * that is actually closed.
 */
const MARKER_LABELS: Record<MarkerEventName, string> = {
  mcp_server_connection: 'MCP server connection',
  hook_execution_complete: 'hook complete',
  hook_execution_start: 'hook start',
  plugin_loaded: 'plugin loaded',
  assistant_response: 'assistant response',
  user_prompt: 'user prompt',
  hook_registered: 'hook registered',
  managed_settings_resolved: 'settings resolved',
  retention_sweep: 'retention sweep',
};

/**
 * Event names are an OPEN set from an untrusted producer, so this must NOT have
 * a `never` arm: an unmapped name is ordinary data, not a programming error.
 * The typed `default` renders the raw bare name readably and invents nothing.
 */
export function markerLabel(event: string): string {
  const mapped = (MARKER_LABELS as Record<string, string | undefined>)[event];
  return mapped ?? event.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Producer — a CLOSED union, so this switch is exhaustive (issue #33, spec
// §5.3).
// ---------------------------------------------------------------------------

/**
 * One line naming WHO emitted this session, or `null` to render nothing.
 *
 * WHY THIS IS READ AT ALL. The field existed, typed, for the whole life of the
 * trace plane and was rendered by nothing — which is how it went on reporting
 * every conforming third-party producer as Claude Code without a single test
 * being able to see it. Rendering it is what makes the union observable from
 * the consumer side; it is not decoration.
 *
 * THE COPY REPORTS A CLAIM, NEVER A VERDICT (spec §7). Nothing authenticates a
 * producer from its own attributes: a record is treated as a Claude Code claim
 * because it carries the `claude_code.` prefix, which any third party may
 * choose to send. `conformant` is equally not an approval — it says the records
 * matched the documented shape and claimed nothing. Never write "verified",
 * "trusted", "certified" or "official" here.
 *
 * TWO guards, the same pair as `spanStateLabel` and for the same two edits:
 * the `never` arm makes a fourth member added to the MIRRORED union, here
 * inside `dashboard/`, a type error; the runtime arm handles the edit `tsc`
 * cannot see — the server widening the union while this mirror is updated later
 * or not at all. It must NOT throw: there is no error boundary anywhere in
 * `dashboard/src`, so a throw in here white-screens the whole Runs tab instead
 * of printing one unfamiliar word. So it degrades to the raw value, which is
 * honest and invents nothing. The mirror is pinned by
 * `test/trace-console-mirror.test.ts` in the root suite.
 */
export function producerLabel(producer: AgentTrace['producer']): string | null {
  switch (producer) {
    case 'claude_code':
      return 'Producer: self-declared as Claude Code. Nothing here verifies that claim.';
    case 'conformant':
      // BOTH HALVES SCOPED TO THE RECORDS THAT WERE READ. The earlier wording —
      // "emitted the lifecycle contract without claiming to be Claude Code" —
      // was wrong twice over. It read as WHOLE-STREAM conformance when ONE
      // mapped record satisfies the condition; and it denied a claim it cannot
      // deny, because the claim is counted only on MAPPED records, so a session
      // whose UNMAPPED record carried the `claude_code.` prefix is `conformant`
      // while having claimed exactly that. `resolveEventName` strips the prefix,
      // so that claim is invisible in `unrecognized_events` too — the copy was
      // denying something the reader has no way to check. Say less, and scope it.
      return 'Producer: the records we read matched the lifecycle contract, and none of those '
        + 'carried a Claude Code marker. Records we did not read are not covered by this.';
    case null:
      // `null` iff this session mapped nothing, which takes `TraceBody`'s
      // empty-state branch — so this renders nothing rather than gaining a
      // second message beside the one already there.
      return null;
    default: {
      const _exhaustive: never = producer;
      return `Producer: ${String(_exhaustive)}`;
    }
  }
}

// ---------------------------------------------------------------------------
// Geometry. All in viewBox units; the SVG scales uniformly.
// ---------------------------------------------------------------------------

/** Mirrors `TRACE_MAX_RECORDS` and `TRACE_MAX_RESPONSE_BYTES` in
 *  `lib/lambdas/shared/agent-trace.ts` — the TWO bounds behind
 *  `trace.truncated`, quoted to the reader so "incomplete" is a number rather
 *  than an adjective. BOTH are pinned against the server constants by
 *  `test/trace-console-mirror.test.ts` in the root suite; `dashboard/` cannot
 *  import from `lib/`, so a hand-copied number needs a source-scanning test or
 *  it silently misquotes the cap the day the server changes it. */
const TRACE_MAX_RECORDS = 10_000;
const TRACE_MAX_RESPONSE_BYTES = 4_000_000;

const VIEW_W = 1000;
const PLOT_X0 = 4;
const PLOT_X1 = 700;
const PLOT_W = PLOT_X1 - PLOT_X0;
const MARKER_STRIP_H = 18;
const ROW_H = 20;
const BAR_H = 9;
const AXIS_H = 26;
const MIN_BAR_W = 3;

/** Candidate axis steps in ms, coarsest-last. */
const AXIS_STEPS_MS = [
  1_000, 2_000, 5_000, 10_000, 15_000, 30_000, 60_000, 120_000, 300_000, 600_000, 1_800_000,
  3_600_000, 7_200_000, 21_600_000, 86_400_000,
];

/** Up to ~6 labelled ticks on a round interval, always including 0. */
export function axisTicks(spanMs: number): number[] {
  if (!(spanMs > 0)) return [0];
  const step = AXIS_STEPS_MS.find((s) => spanMs / s <= 6) ?? spanMs;
  const out: number[] = [];
  for (let t = 0; t <= spanMs + 1; t += step) out.push(t);
  return out;
}

/** ms since the session's first record, or `null` when unknowable. */
function offsetMs(iso: string | null, originMs: number | null): number | null {
  if (iso === null || originMs === null) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t - originMs : null;
}

/**
 * The axis origin: the earliest RECORD timestamp. `started_at` is excluded on
 * purpose — it is derived (`ended_at − duration_ms`) and on bogus data can
 * precede the first record, which would silently shift every bar left.
 * `summary.session_span_ms` is the axis WIDTH and is taken from the server, not
 * recomputed.
 */
export function traceOriginMs(trace: AgentTrace): number | null {
  let min: number | null = null;
  const consider = (iso: string | null) => {
    if (iso === null) return;
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return;
    if (min === null || t < min) min = t;
  };
  for (const s of trace.spans) {
    consider(s.decided_at);
    consider(s.ended_at);
  }
  for (const m of trace.markers) consider(m.at);
  return min;
}

/** A fraction of the plot, clamped into it. A zero-width session puts every bar
 *  at the origin rather than dividing by zero. */
function xOf(ms: number | null, spanMs: number): number {
  if (ms === null || !(spanMs > 0)) return PLOT_X0;
  const frac = Math.min(1, Math.max(0, ms / spanMs));
  return PLOT_X0 + frac * PLOT_W;
}

// ---------------------------------------------------------------------------
// Tool colour ramp. Series colours are Saga tokens mixed in index.css — never a
// literal hex here, and never anything near --danger, which is reserved for
// failures.
// ---------------------------------------------------------------------------

const SERIES_COUNT = 6;

/** Distinct tool names in first-appearance order — the legend's own order. */
export function toolNames(spans: TraceSpan[]): string[] {
  const seen: string[] = [];
  for (const s of spans) {
    const name = s.tool_name ?? 'unknown tool';
    if (!seen.includes(name)) seen.push(name);
  }
  return seen;
}

/** The most-used tool, or `null` when there are no spans. Ties go to the first
 *  seen, which keeps the tile stable across renders of the same trace. */
export function dominantTool(spans: TraceSpan[]): string | null {
  const counts = new Map<string, number>();
  for (const s of spans) {
    const name = s.tool_name ?? 'unknown tool';
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [name, n] of counts) {
    if (n > bestN) {
      best = name;
      bestN = n;
    }
  }
  return best;
}

function seriesClass(names: string[], tool: string | null): string {
  const i = names.indexOf(tool ?? 'unknown tool');
  return `trace-series-${(i < 0 ? 0 : i) % SERIES_COUNT}`;
}

// ---------------------------------------------------------------------------
// Pieces.
// ---------------------------------------------------------------------------

function Tile({
  label,
  value,
  sub,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  danger?: boolean;
}) {
  return (
    <div className="trace-tile">
      <span className="trace-tile-label">{label}</span>
      <span className={`trace-tile-value${danger ? ' trace-tile-danger' : ''}`}>{value}</span>
      {sub !== undefined && <span className="trace-tile-sub">{sub}</span>}
    </div>
  );
}

/** The right-hand label for one span: `tool · duration`, plus the error type on
 *  a failure and the §4.6 state copy where there is one. */
export function spanLabel(span: TraceSpan): string {
  const parts = [span.tool_name ?? 'unknown tool', fmtMs(span.duration_ms)];
  // `ok === false`, never `!span.ok`: `ok: null` is UNKNOWN, and treating it as
  // a failure is the fabricated-failure trap of spec §4.5.
  if (span.ok === false && span.error_type !== null) parts.push(span.error_type);
  const state = spanStateLabel(span.state);
  if (state !== null) parts.push(state);
  if (span.attrs_truncated) parts.push('attributes truncated');
  return parts.join(' · ');
}

function SpanRow({
  span,
  index,
  spanMs,
  originMs,
  names,
}: {
  span: TraceSpan;
  index: number;
  spanMs: number;
  originMs: number | null;
  names: string[];
}) {
  const y = MARKER_STRIP_H + index * ROW_H;
  const startMs = offsetMs(span.started_at ?? span.decided_at, originMs);
  const endMs = offsetMs(span.ended_at, originMs);
  const x0 = xOf(startMs, spanMs);
  // An unterminated span has no end at all, so its bar runs to the right edge
  // of the plot behind a fade — visibly unlike a complete one, and without
  // claiming a duration nothing measured.
  const x1 = span.state === 'unterminated' ? PLOT_X1 : Math.max(xOf(endMs, spanMs), x0 + MIN_BAR_W);
  const failed = span.ok === false;
  const cls = [
    'trace-bar',
    spanStateClass(span.state),
    failed ? 'trace-bar-failed' : seriesClass(names, span.tool_name),
  ].join(' ');
  return (
    <g className="trace-row">
      <rect
        className={cls}
        x={x0}
        y={y + (ROW_H - BAR_H) / 2}
        width={Math.max(MIN_BAR_W, x1 - x0)}
        height={BAR_H}
        rx={2}
      />
      <text className="trace-bar-label" x={Math.min(x1 + 8, VIEW_W - 4)} y={y + ROW_H / 2 + 3.5}>
        {spanLabel(span)}
      </text>
    </g>
  );
}

function MarkerTicks({
  markers,
  spanMs,
  originMs,
}: {
  markers: TraceMarker[];
  spanMs: number;
  originMs: number | null;
}) {
  return (
    <g className="trace-markers">
      {markers.map((m, i) => {
        const x = xOf(offsetMs(m.at, originMs), spanMs);
        return (
          <rect
            key={`${m.at}#${m.event}#${i}`}
            className="trace-tick"
            x={x}
            y={2}
            width={1.5}
            height={MARKER_STRIP_H - 6}
          />
        );
      })}
    </g>
  );
}

/** The lifecycle markers as a plain list under the timeline. Attribute VALUES
 *  go through `String()` and nothing else — see the file header. */
function MarkerList({ markers }: { markers: TraceMarker[] }) {
  return (
    <details className="trace-markers-list">
      <summary>
        {markers.length} lifecycle {markers.length === 1 ? 'event' : 'events'}
      </summary>
      <ul>
        {markers.map((m, i) => (
          <li key={`${m.at}#${m.event}#${i}`} className="trace-marker-item">
            {/* `at` is `string | null` (spec §5) — an em dash, never an empty
                cell, so an unreadable clock reads as absent rather than as a
                rendering bug. */}
            <span className="trace-marker-at mono">{m.at ?? '—'}</span>
            <span className="trace-marker-event">{markerLabel(m.event)}</span>
            {Object.entries(m.attrs).map(([k, v]) => (
              <span key={k} className="trace-marker-attr mono">
                {k}={String(v)}
              </span>
            ))}
            {m.attrs_truncated && (
              <span className="truncated-marker">attributes truncated</span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function Legend({ names, hasFailure }: { names: string[]; hasFailure: boolean }) {
  return (
    <div className="trace-legend">
      {names.map((n, i) => (
        <span key={n} className="trace-legend-item">
          <span className={`trace-legend-swatch trace-series-${i % SERIES_COUNT}`} />
          {n}
        </span>
      ))}
      {hasFailure && (
        <span className="trace-legend-item">
          <span className="trace-legend-swatch trace-bar-failed" />
          failure
        </span>
      )}
      <span className="trace-legend-item">
        <span className="trace-legend-swatch trace-tick" />
        lifecycle event
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The timeline.
// ---------------------------------------------------------------------------

function Timeline({ trace }: { trace: AgentTrace }) {
  const spanMs = trace.summary.session_span_ms;
  const originMs = traceOriginMs(trace);
  const names = toolNames(trace.spans);
  const bodyH = MARKER_STRIP_H + trace.spans.length * ROW_H;
  const height = bodyH + AXIS_H;
  return (
    <svg
      className="trace-svg"
      viewBox={`0 0 ${VIEW_W} ${height}`}
      width="100%"
      // No `height`: with one, the default `xMidYMid meet` scales the content to
      // the width and CENTRES it vertically, leaving blank bands above and below.
      preserveAspectRatio="xMinYMin meet"
      role="img"
      aria-label="Agent trace timeline"
    >
      <MarkerTicks markers={trace.markers} spanMs={spanMs} originMs={originMs} />
      {trace.spans.map((s, i) => (
        <SpanRow
          key={`${s.tool_use_id ?? 'span'}#${s.sequence ?? i}#${i}`}
          span={s}
          index={i}
          spanMs={spanMs}
          originMs={originMs}
          names={names}
        />
      ))}
      <line className="trace-axis" x1={PLOT_X0} y1={bodyH + 2} x2={PLOT_X1} y2={bodyH + 2} />
      {axisTicks(spanMs).map((t) => (
        <text key={t} className="trace-axis-label" x={xOf(t, spanMs)} y={bodyH + 16}>
          {fmtAxis(t)}
        </text>
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// The panel.
// ---------------------------------------------------------------------------

export default function TracePanel({
  producer,
  sessionId,
}: {
  /** The run's ADDRESS (a Guardius identity_id). Not the same thing as the
   *  trace body's own `producer` field, which says what KIND of emitter the
   *  records claimed to come from — see `producerLabel`. */
  producer: string;
  sessionId: string;
}) {
  // Both pieces of state are keyed BY SESSION, not booleans. `RunDetail` is
  // rendered unkeyed, so a future edit that swaps one run's detail view for
  // another's in place would reuse this component instance — and a bare
  // `everOpened` boolean would then arrive at the next run already latched
  // `true`, firing the full-prefix read on a COLLAPSED panel. That is exactly
  // the eager fetch spec §6 forbids, so the guard lives here rather than
  // depending on a `key` in the parent. The producer is part of the key because
  // a session id is unique only within its producer.
  const sessionKey = `${producer}#${sessionId}`;
  const [openFor, setOpenFor] = useState<string | null>(null);
  // Latched, NOT derived from `open`: once fetched the trace is cached, so
  // collapsing and re-opening the SAME session must not re-read S3.
  const [latchedFor, setLatchedFor] = useState<string | null>(null);
  const open = openFor === sessionKey;
  const { data, isLoading, isError, refetch } = useTrace(
    producer,
    sessionId,
    latchedFor === sessionKey,
  );

  const toggle = () => {
    setOpenFor(open ? null : sessionKey);
    setLatchedFor(sessionKey);
  };

  return (
    <>
      <h3 className="section-title">Agent trace</h3>
      <button
        type="button"
        className="btn trace-disclosure"
        aria-expanded={open}
        onClick={toggle}
      >
        {open ? '▾' : '▸'} {open ? 'Hide' : 'Show'} tool-call timeline
      </button>

      {open &&
        (isLoading ? (
          <p className="note">Loading trace…</p>
        ) : isError ? (
          <p className="error" role="alert">
            Failed to load trace{' '}
            <button type="button" className="btn" onClick={() => refetch()}>
              Retry
            </button>
          </p>
        ) : data === undefined ? (
          <p className="note">No trace for this run.</p>
        ) : (
          <TraceBody trace={data} />
        ))}
    </>
  );
}

function TraceBody({ trace }: { trace: AgentTrace }) {
  const { summary, spans, markers, unrecognized_events: unrecognized } = trace;

  // An empty trace is a VALID response, and it must never render as a blank
  // panel: when nothing was recognised, name what was seen instead (spec §6).
  if (spans.length === 0 && markers.length === 0) {
    return unrecognized.length > 0 ? (
      <div className="trace-empty">
        <p className="note">No recognized producer events in this run.</p>
        <p className="note trace-unrecognized">
          Events seen but not mapped: <span className="mono">{unrecognized.join(', ')}</span>
        </p>
      </div>
    ) : (
      <p className="note">No trace records for this run.</p>
    );
  }

  const dominant = dominantTool(spans);
  const names = toolNames(spans);
  const anyFailure = spans.some((s) => s.ok === false);

  const producer = producerLabel(trace.producer);

  return (
    <div className="trace-body">
      {producer !== null && <p className="note trace-producer">{producer}</p>}

      <div className="trace-tiles">
        <Tile label="Session" value={fmtMs(summary.session_span_ms)} sub="wall clock" />
        <Tile
          label="Tool execution"
          value={fmtMs(summary.tool_busy_ms)}
          sub={`${fmtRatio(summary.tool_busy_ratio)} of wall clock`}
        />
        <Tile
          label="Tool calls"
          value={String(summary.tool_call_count)}
          sub={dominant !== null ? `mostly ${dominant}` : undefined}
        />
        <Tile
          label="Failures"
          value={String(summary.failure_count)}
          danger={summary.failure_count > 0}
          sub={
            summary.unterminated_count > 0
              ? `${summary.unterminated_count} with no result recorded`
              : undefined
          }
        />
      </div>

      <Legend names={names} hasFailure={anyFailure} />
      <Timeline trace={trace} />

      <p className="note trace-insight">
        Tools were executing for {fmtRatio(summary.tool_busy_ratio)} of this session. The gaps
        between the bars are model inference — the part cost telemetry prices but never locates.
      </p>

      {trace.truncated && (
        <p className="note trace-caveat">
          {/* TWO bounds reach this flag, so the copy must not quote only one of
              them: the record cap, and a byte budget on the derived trace that
              a marker-heavy session reaches first. Naming the record count
              alone read as a measured fact and was false whenever the byte
              budget did the cutting. */}
          This trace is not the whole session: it was derived from the session&apos;s earliest
          records in order, bounded at {TRACE_MAX_RECORDS.toLocaleString()} records or{' '}
          {Math.round(TRACE_MAX_RESPONSE_BYTES / 1_000_000)} MB of derived trace, whichever came
          first. Later records are not shown.
        </p>
      )}

      {(summary.unterminated_count > 0 || summary.truncated_records > 0) && (
        <p className="note trace-caveat">
          A span with no result recorded is not evidence about the agent: Watchtower&apos;s own
          attribute cap, its 256-records-per-request ingest cap, the INFO severity floor and
          either of this view&apos;s own two bounds can each drop a result record.
          {summary.truncated_records > 0 &&
            (summary.truncated_records === 1
              ? ' 1 record in this session had its attributes truncated,'
              : ` ${summary.truncated_records} records in this session had their attributes truncated,`) +
              ' so that cause may be in play here.'}
        </p>
      )}

      {markers.length > 0 && <MarkerList markers={markers} />}

      {unrecognized.length > 0 && (
        <p className="note trace-unrecognized">
          Events seen but not mapped: <span className="mono">{unrecognized.join(', ')}</span>
        </p>
      )}
    </div>
  );
}
